import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { loadOrder, lockRXDetails, getLockedItems } from '../../Services/loadOrder'; 
import { columns } from '../../dummyData/datacol';
import MenuBar from './MenuBar';
import { useNavigate, useLocation } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import toast,{ Toaster } from 'react-hot-toast';
import '../../assets/css/home.css';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

export default function NeedsTrackingGrid() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);  
  const [lockedTaskIds, setLockedTaskIds] = useState([]);
  const { state } = useLocation(); 
  const [names, setNames] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [showInitialSpinner, setShowInitialSpinner] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));



  useEffect(() => {
    const searchFields = columns.map(col => ({
      label: col.headerName,
      field: col.searchField || col.field
    }));
    setNames(searchFields);
  }, []);

  const handleSearch = async () => {
    if (!selectedName || !searchText) {
      toast.error("Please select a value and enter text to search.");
      return;
    }

    const user = JSON.parse(localStorage.getItem('user'));
    const pharmacyId = user?.pharmacyId;
    const userId = user?.userId;

    if (!pharmacyId || !userId) {
      toast.error("Missing pharmacy or user info. Please login again.");
      navigate('/error-page');
      return;
    }

    setSearchLoading(true);
    try {
      const orderResponse = await loadOrder(pharmacyId, "NEEDS", selectedName, searchText, userId, "");
      if (orderResponse.success && Array.isArray(orderResponse.data)) {
        const lockedResponse = await getLockedItems(pharmacyId);
        const lockedTaskMap = new Map();
        if (lockedResponse.success) {
          lockedResponse.data?.TaskDetail?.forEach(item => {
            lockedTaskMap.set(item.TaskId, item.UserID);
          });
          setLockedTaskIds(Array.from(lockedTaskMap.keys()));
        }

        const formattedRows = orderResponse?.filterData.map((item, index) => ({
          id: index + 1,
          taskId: item.TaskId,
          priority: item.Priority || '',
          authNo: item.AuthNo || '',
          from: item.From || '',
          receivedDate: item.ReceivedDate
          ? new Date(item.ReceivedDate).toLocaleDateString()
          : '',
          medStartDate: item.MedStartDate
            ? new Date(item.MedStartDate).toLocaleDateString()
            : '',
          taskName: item.TaskName || '',
          memberId: item.MemberId || '',
          lastName: item.LastName || '',
          firstName: item.FirstName || '',
          preferredName: item.PreferredName || '',
          contract: item.Contract || '',
          vendorData: item.VendorData || '',
          isLocked: lockedTaskMap.has(item.TaskId),
          lockedBy: lockedTaskMap.get(item.TaskId) || '',
          pharmCode: item.PharmCode || ''
        }));
        setRows(formattedRows);
      } else {
        setRows([]);
        toast.error("No data found for the search.");
      }
    } catch (error) {
      
      toast.error("Error searching cost share details.");
      navigate('/error-page');
    } finally {
      setSearchLoading(false);
    }
  };
  

  useEffect(() => {
    const fetchOrders = async () => {
      const user = JSON.parse(localStorage.getItem('user'));
      const pharmacyId = user?.pharmacyId;
      const userId = user?.userId;
  
      if (!pharmacyId) return;
  
      const [orderResponse, lockedResponse] = await Promise.all([
        loadOrder(pharmacyId, "NEEDS", "", "", userId, ""),
        getLockedItems(pharmacyId)
      ]);
  
      let lockedTaskMap = new Map();
      if (lockedResponse.success) {
        console.log("Locked Tasks:", lockedResponse.data?.TaskDetail);
        lockedResponse.data?.TaskDetail?.forEach(item => {
          lockedTaskMap.set(item.TaskId, item.UserID);
        });
        setLockedTaskIds(Array.from(lockedTaskMap.keys())); // still store this if needed elsewhere
      }
  
      if (orderResponse.success) {
        const formattedRows = orderResponse.data.map((item, index) => ({
          id: index + 1,
          taskId: item.TaskId,
          priority: item.Priority || '',
          authNo: item.AuthNo || '',
          from: item.From || '',
          receivedDate: item.ReceivedDate
          ? new Date(item.ReceivedDate).toLocaleDateString()
          : '',
          medStartDate: item.MedStartDate
            ? new Date(item.MedStartDate).toLocaleDateString()
            : '',
          taskName: item.TaskName || '',
          memberId: item.MemberId || '',
          lastName: item.LastName || '',
          firstName: item.FirstName || '',
          preferredName: item.PreferredName || '',
          contract: item.Contract || '',
          vendorData: item.VendorData || '',
          isLocked: lockedTaskMap.has(item.TaskId),
          lockedBy: lockedTaskMap.get(item.TaskId) || ''
        }));
        setRows(formattedRows);
      }
  
      setLoading(false);
    };
  
    fetchOrders();
  }, []);
  

  const handleRowClick = async (params) => {
    const { isLocked } = params.row;
    const lockedBy = params.row.lockedBy;
    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user?.userId;
    const pharmacyId = user?.pharmacyId;

    if (!userId || !pharmacyId) {
      toast.error("User session missing. Please login again.");
      navigate('/error-page');
      return;
    }

    if (isLocked) {
      toast.error(`This task is currently locked by ${lockedBy}.`);
      return;
    }

    const { taskId, contract } = params.row;

    try {
      const lockResult = await lockRXDetails(contract, userId, taskId, pharmacyId, "LOCK");
      


      if (lockResult.success) {
        navigate('/needs-open-orders', {
          state: {
            taskId,
            contract,
            pharmacyId,
            userId
          }
        });
      } else {
        toast.error(lockResult.message || "This task is currently being used by another user.");
      }
    } catch (err) {
      console.error("Error during lock attempt:", err);
      toast.error("Unexpected error occurred while locking the task.");
      navigate('/error-page');
    }
  };

  return (
    <div>
      <Toaster position='top-right'/>
      <MenuBar />
      <div className='top-page-option'>
        <div className='top-page-option-name' style={{ display: 'flex', alignItems: 'center' }}>
          <a 
            href="#/home" 
            style={{
              textDecoration: 'none',
              color: '#ec088c',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            <ChevronLeft color='#ec088c' size={18} />
            <span style={{ marginLeft: '5px' }}>Back</span>
          </a>
        </div>

        <div className='top-page-option-name' style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span>New Orders</span>
                  <ChevronRight color='#ec088c' size={18} style={{ position: 'relative', top: '2px' }} />
                  <span>Needs Tracking</span>
                
        <div style={{ display: 'flex',justifyContent:'right', alignItems: 'center', gap: '8px' ,marginLeft:'auto', marginRight:'6.2%' }}>  
            {/* Dropdown, Textbox, and Search Button */}
            <FormControl size="small" sx={{ minWidth: 100, height: '30px', background: '#fff', borderRadius: '4px', marginRight: '10px' }}>
      <InputLabel sx={{ fontFamily: 'Calibri', marginBottom: '10px'
        , background: '#fff', color: 'black', fontSize: '12px'}}>--Select--</InputLabel>
      <Select
        value={selectedName}
        onChange={(e) => setSelectedName(e.target.value)}
        label="Select"
        sx={{
          fontFamily: 'Segoe ui',
          fontSize: '12px',
          border: '1px solid rgb(236, 151, 202)',
          borderRadius: '15px',
          height: '30px',
          boxShadow: 'none',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ec008c',
            boxShadow: '0 0 6px rgba(236, 0, 140, 0.4)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ec008c',
          },
          
        }}
      >
        <MenuItem value=""
        sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
          --Select--</MenuItem>
        <MenuItem value="Priority"sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>
        Priority</MenuItem>
        <MenuItem value="AuthNo" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>
        AuthNo</MenuItem>
        <MenuItem value="MedStartDate" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>
        MedStartDate</MenuItem>
        {/* <MenuItem value="TaskName" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>
        TaskName</MenuItem> */}
        <MenuItem value="MemberID" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>
        MemberID</MenuItem>
        <MenuItem value="LastName" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>
        LastName</MenuItem>
        <MenuItem value="FirstName" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>
        FirstName</MenuItem>
      </Select>
    </FormControl>


            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
              style={{ padding: '5px 5px', borderRadius: '15px', 
                border: '1px solid rgb(236, 151, 202)', outline: 'none',
                boxShadow : '0 1px 2px rgb(236, 151, 202)',
        transition: 'all 0.3s ease'
      }}
      onFocus={(e) => {
        e.target.style.border = '1px solid #ec008c';
        e.target.style.boxShadow = '0 0 6px rgba(236, 0, 140, 0.4)';
      }}
      onBlur={(e) => {
        e.target.style.border = '1px solid rgb(236, 151, 202)';
        e.target.style.boxShadow = '0 1px 2px rgb(236, 151, 202)';
      }}
            />
            <button
              onClick={handleSearch}
              disabled={searchLoading}
              style={{ padding: '6px 18px', borderRadius: '4px', background: '#ec008c', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              Search
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: '5px 18px', borderRadius: '4px', background: '#ec008c', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              View All
            </button>
          </div>
          </div>
      </div>
      <div style={{ height: 'auto', width: '85%', margin: 'auto' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          onRowClick={handleRowClick}
          getRowClassName={(params) => {
            // console.log("Row Check:", params.row);
            return params.row.isLocked ? 'locked-task-row' : '';
          }}                    
          localeText={{ noRowsLabel: 'No Records Found, Please search again.' }}                  
          sx={{
            fontSize: '12px',
            fontFamily: 'Segoe UI',
            '& .pink-header': {
              color: '#fff',
              backgroundColor: '#ec008c',
              fontWeight: 'bold'
            },
            
            '& .locked-task-row': {
              backgroundColor: 'cyan !important',
              color: 'black'
            }, '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f5f5f5 !important',
              cursor: 'pointer',
            },// Remove blue outline on cell focus
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
    // Remove blue outline on column header focus
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none !important',
      boxShadow: 'none !important',
    },
          }}
        />
      </div>
      {showSpinner && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            border: '6px solid #f3f3f3',
            borderTop: '6px solid #EC088C',
            borderRadius: '50%',
            width: 48,
            height: 48,
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{marginTop: 24, fontSize: '1.2rem', fontWeight: 500, color: '#222'}}>Your request is being processed</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}
      {loading && !showSpinner && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            border: '6px solid #f3f3f3',
            borderTop: '6px solid #EC088C',
            borderRadius: '50%',
            width: 48,
            height: 48,
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{marginTop: 24, fontSize: '1.2rem', fontWeight: 500, color: '#222'}}>Your request is being processed</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}
      {searchLoading && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 3000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            border: '6px solid #f3f3f3',
            borderTop: '6px solid #EC088C',
            borderRadius: '50%',
            width: 48,
            height: 48,
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{marginTop: 24, fontSize: '1.2rem', fontWeight: 500, color: '#222'}}>Searching...</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}
    </div>
  );
}
